import { transformCart } from "../../cart/helpers/cartApiHelpers/transformCart";
import { formatPrice } from "../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../common/helpers/currency";
import type { RawOrderResponse } from "../../../../types/api/wcStoreApi/wcStoreApiOrderEndpointsTypes";
import type { TransformedOrder } from "../types/orderApiTypes";

interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * Transforms the raw Order API response.
 * Uses the transformCart logic as a base and enriches it with order-specific metadata.
 */
export const transformOrderResponse = (
  response: RawOrderResponse,
  languageInfo: LanguageInfo,
): TransformedOrder => {
  // Transform the order using the standard cart schema first.
  const transformedCartData = transformCart(response as any, languageInfo);

  // --- LINE-ITEM BASED SUBTOTAL RECONSTRUCTION ---
  /*
   * WooCommerce's Order API returns the 'total_items' value AFTER discounts have been applied.
   * To maintain visual consistency with the Cart/Checkout pages (where Subtotal represents the
   * pre-discount original total), we manually reconstruct the subtotal by summing up the
   * 'line_subtotal' and 'line_subtotal_tax' of each individual order item.
   *
   * This approach relies directly on the native WooCommerce database values, preventing the
   * 1-2 cent rounding discrepancies that are commonly introduced when trying to reverse-calculate
   * the subtotal backward from a rounded grand total using a static tax factor.
   */
  const currencyInfo = extractCurrencyInfo(response.totals);

  let originalNet = 0;
  let originalTax = 0;

  response.items.forEach((item) => {
    originalNet += parseInt(item.totals.line_subtotal || "0", 10);
    originalTax += parseInt(item.totals.line_subtotal_tax || "0", 10);
  });

  const grossSubtotal = originalNet + originalTax;

  /*
   * Override only the item subtotal fields.
   * Discounts, Shipping, Fees, Tax, and Grand Total remain untouched and are mapped
   * directly from the raw database records to maintain absolute alignment with payment processors.
   */
  transformedCartData.totals.totalItems = formatPrice(
    String(grossSubtotal),
    currencyInfo,
  );
  transformedCartData.totals.totalItemsNet = formatPrice(
    String(originalNet),
    currencyInfo,
  );
  transformedCartData.totals.totalItemsTax = formatPrice(
    String(originalTax),
    currencyInfo,
  );

  // Patch item IDs specifically for orders (resolving Variation vs Product ID)
  const patchedItems = transformedCartData.items.map((item, index) => {
    const rawItem = response.items[index];
    const idsExtension = rawItem.extensions?.item_ids;

    if (idsExtension) {
      const realId =
        idsExtension.variation_id > 0
          ? idsExtension.variation_id
          : idsExtension.product_id;

      return {
        ...item,
        id: realId,
        parentId: idsExtension.parent_id,
      };
    }
    return item;
  });

  const additionalInfo = response.extensions?.additional_order_info;
  const agreementsSnapshot = response.extensions?.legal_agreements_snapshot;
  const appointmentData = response.extensions?.appointment;

  let formattedAppointmentDate = appointmentData?.date || "";
  if (formattedAppointmentDate) {
    try {
      formattedAppointmentDate = new Date(
        formattedAppointmentDate,
      ).toLocaleDateString(languageInfo.currentLanguage.replace("_", "-"), {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      formattedAppointmentDate = appointmentData!.date;
    }
  }

  let formattedDate = "";
  if (additionalInfo?.date_created) {
    try {
      formattedDate = new Date(additionalInfo.date_created).toLocaleDateString(
        languageInfo.currentLanguage.replace("_", "-"),
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
      );
    } catch (e) {
      formattedDate = additionalInfo.date_created.split("T")[0];
    }
  }

  return {
    ...transformedCartData,
    items: patchedItems,
    id: response.id,
    status: response.status,
    orderNumber: additionalInfo?.order_number || String(response.id),
    dateCreated: formattedDate,
    customerNote: additionalInfo?.customer_note || "",
    paymentMethodInfo: {
      methodId: additionalInfo?.payment_method_info?.method_id || "",
      title: additionalInfo?.payment_method_info?.title || "",
      instructions: additionalInfo?.payment_method_info?.instructions || "",
      accountDetails:
        additionalInfo?.payment_method_info?.account_details || [],
    },
    legalAgreementsSnapshot: agreementsSnapshot
      ? {
          preliminaryHtml: agreementsSnapshot.preliminary_html,
          salesHtml: agreementsSnapshot.sales_html,
        }
      : undefined,
    appointmentSnapshot: appointmentData
      ? {
          date: appointmentData.date,
          formattedDate: formattedAppointmentDate,
          time: appointmentData.time,
        }
      : undefined,
  };
};
